import 'views/components/common/tag-selector/index.css'
import 'react-select/dist/react-select.css'
import './index.css'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import Select from 'react-select'
import type { NativeTagWithCategory } from 'types/track'
import type { DropdownOption } from 'types/ui'
import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getFlattenedNativeTags } from '@disco/state-management/selectors/tags'
import { getPopoverDropdownStyle } from '@disco/utils/dom'
import NativeTagsPopover from 'components/common/native-tags-popover'
import Icon from 'components/ui/icon'
import Tag from 'components/ui/tag'
import useEventListener from 'hooks/use-event-listener'

const mapStateToProps = (state): any => {
  return {
    tags: getFlattenedNativeTags(state),
  }
}

type Props = {
  onChange: (tags: DropdownOption[]) => void
  selectedTags: number[]
  tags: NativeTagWithCategory[]
  placeholder: string
  disabled?: boolean
  className?: string
  disabledTags?: string[]
  scrollContainer?: HTMLDivElement | null | undefined
}

/**
 * Native tags selector component with search and multi-selection functionality
 * Integrates with react-select and displays tags with color indicators
 * Supports scrollable containers and custom positioning
 * @param onChange - Callback when tag selection changes
 * @param selectedTags - Array of selected tag IDs
 * @param tags - Available tags with categories
 * @param placeholder - Placeholder text for the selector
 * @param disabled - Whether the selector is disabled
 * @param className - Additional CSS classes
 * @param disabledTags - Array of disabled tag names
 * @param scrollContainer - Container for scroll-aware positioning
 */
const NativeTagsSelector = (props: Props) => {
  const {
    selectedTags,
    disabled,
    className,
    placeholder,
    onChange,
    tags,
    scrollContainer,
  } = props
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const tagOptions = useMemo(
    () =>
      tags.map(t => {
        return {
          // `value` is for displaying `selectedTags`
          value: t.id,
          // `tag` is for rendering `valueComponent`
          tag: t,
          //
          // TODO: find a way to avoid double search:
          //   1. react-select filters everything
          //   2. NativeTagsPopover filters everything
          //   ideally (1) should be disabled
          //
          // `label` is for search
          label: t.name + ' - ' + t.category.name,
        }
      }),
    [tags],
  )

  const arrowRenderer = () => <Icon type="arrow-down" />

  const [popoverStyle, setPopoverStyle] = useState<
    React.CSSProperties | undefined
  >(undefined)
  useEffect(() => {
    if (containerRef.current) {
      const position = scrollContainer ? 'fixed' : 'absolute'
      setPopoverStyle(getPopoverDropdownStyle(containerRef.current, position))
    }
  }, [scrollContainer, containerRef])
  const scrollHandler = useCallback(() => {
    if (!containerRef.current || !scrollContainer) return null
    setPopoverStyle(getPopoverDropdownStyle(containerRef.current, 'fixed'))
  }, [scrollContainer, containerRef])
  useEventListener('scroll', scrollHandler, scrollContainer)
  useEventListener('resize', scrollHandler)

  const menuRenderer = props => {
    return (
      <div style={popoverStyle}>
        <NativeTagsPopover inReactSelect={props} />
      </div>
    )
  }

  const valueComponent = props => {
    const { value, onRemove } = props

    const onRemoveClick = () => {
      onRemove(value)
    }

    return (
      <span className="Select-tag">
        <Tag
          tag={value.tag.name}
          tagColor={colorIndicatorsEnabled ? value.tag.color : undefined}
          onRemoveClick={disabled ? undefined : onRemoveClick}
        />
      </span>
    )
  }

  return (
    <div className="NativeTagsSelector" ref={containerRef}>
      <Select
        arrowRenderer={arrowRenderer}
        menuRenderer={menuRenderer}
        valueComponent={valueComponent}
        className={className}
        multi
        clearable={false}
        options={tagOptions}
        matchProp="label"
        value={selectedTags}
        placeholder={placeholder}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  )
}

export default connect(mapStateToProps)(NativeTagsSelector) as any
